import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Users,
  BarChart3,
  Archive,
  Edit,
  FolderKanban,
  Settings2,
} from 'lucide-react'
import { useState } from 'react'

import { TeamForm } from '@/components/pm/team/team-form'
import { TeamMembers } from '@/components/pm/team/team-members'
import { TeamProjects } from '@/components/pm/team/team-projects'
import { TeamStageOwners } from '@/components/pm/team/team-stage-owners'
import { TeamStats } from '@/components/pm/team/team-stats'
import { useConfirmDialog } from '@/hooks'
import {
  useTeam,
  useArchiveTeam,
  type TeamStatus,
} from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 团队详情页面
 */
export const Route = createFileRoute('/_authenticated/pm/teams/$teamId/')({
  component: TeamDetailPage,
})

const STATUS_LABELS: Record<TeamStatus, string> = {
  active: '活跃',
  archived: '已归档',
}

const STATUS_COLORS: Record<TeamStatus, string> = {
  active: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-800',
}

type TabType = 'members' | 'projects' | 'stage-owners' | 'stats'

function TeamDetailPage() {
  const params = Route.useParams()
  const teamId = params.teamId
  const navigate = useNavigate()
  const [showEditForm, setShowEditForm] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('members')

  const { data: team, isLoading, error } = useTeam(teamId)
  const archiveMutation = useArchiveTeam()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleArchive = async () => {
    const confirmed = await confirmWithPreset(
      'archive',
      '确定要归档此团队吗？归档后团队下的所有项目也将被归档。'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await archiveMutation.mutateAsync(teamId)
        void navigate({ to: '/pm/teams' })
      } finally {
        setLoading(false)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (error || !team) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-destructive">团队不存在或加载失败</div>
        <Link
          to="/pm/teams"
          className="text-sm text-primary hover:underline"
        >
          返回团队列表
        </Link>
      </div>
    )
  }

  const tabs: { key: TabType; label: string; icon: React.ReactNode }[] = [
    { key: 'members', label: '成员', icon: <Users className="h-4 w-4" /> },
    { key: 'projects', label: '项目', icon: <FolderKanban className="h-4 w-4" /> },
    { key: 'stage-owners', label: '阶段负责人', icon: <Settings2 className="h-4 w-4" /> },
    { key: 'stats', label: '统计', icon: <BarChart3 className="h-4 w-4" /> },
  ]

  return (
    <div className="space-y-6">
      {/* 返回和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => void navigate({ to: '/pm/teams' })}
          className="rounded-md p-2 hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                STATUS_COLORS[team.status]
              )}
            >
              {STATUS_LABELS[team.status]}
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{team.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {team.status !== 'archived' && (
            <>
              <button
                onClick={() => setShowEditForm(true)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
              >
                <Edit className="h-4 w-4" />
                编辑
              </button>
              <button
                onClick={() => void handleArchive()}
                disabled={archiveMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                <Archive className="h-4 w-4" />
                归档
              </button>
            </>
          )}
        </div>
      </div>

      {/* 团队描述 */}
      {team.description && (
        <p className="text-muted-foreground">{team.description}</p>
      )}

      {/* Tab 导航 */}
      <div className="border-b">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab 内容 */}
      <div>
        {activeTab === 'members' && <TeamMembers teamId={teamId} isArchived={team.status === 'archived'} />}
        {activeTab === 'projects' && <TeamProjects teamId={teamId} isArchived={team.status === 'archived'} />}
        {activeTab === 'stage-owners' && <TeamStageOwners teamId={teamId} isArchived={team.status === 'archived'} />}
        {activeTab === 'stats' && <TeamStats teamId={teamId} />}
      </div>

      {/* 编辑表单弹窗 */}
      {showEditForm && (
        <TeamForm
          team={team}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => setShowEditForm(false)}
        />
      )}
      <ConfirmDialog />
    </div>
  )
}
